import { css } from '@emotion/react';
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react';

import { Notification } from '@features/enterpriseNotifications/types';
import NotificationListItem from '@features/enterpriseNotifications/components/NotificationListItem/NotificationListItem';
import { staticFileLocation } from '@server/constants';
import { useCreateApplicationNote } from '@features/enterpriseNotifications/hooks/useReadNotification';
import { Close } from '@mui/icons-material';
import { useEnterpriseGetNotifications } from '@features/enterpriseNotifications/hooks/useGetNotifications';
import { LoadingButton } from '@mui/lab';
import {
  getGroupSummaryText,
  groupNotificationsSmart,
} from '@features/notifications/components/NotificationListItem/utils';
import NotificationIcon from '../NotificationIcon/NotificationIcon';
import { ArrowDropDown } from '@mui/icons-material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';

type NotificationsModalProps = {
  isOpen: boolean;
  handleClose: () => void;
  notifications: Notification[];
  setNotificationsData: Dispatch<SetStateAction<Notification[]>>;
  notificationsData: Notification[];
  
};

const NotificationsModal: FC<NotificationsModalProps> = ({
  isOpen,
  handleClose,
  setNotificationsData,
  notificationsData,
}) => {
  const {
    enterpriseNotificationsData,
    isNotificationsLoading,
    isNotificationsFetched,
  } = useEnterpriseGetNotifications({});
  const enterpriseNotification =
    enterpriseNotificationsData?.object as Notification[];
  const [errorText, setErrorText] = useState('');
  const [isClearNotificationLoading, setIsClearNotificationLoading] =
    useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {},
  );
  const readNotificationIds = new Set(
    notificationsData.filter((n) => n.is_read).map((n) => n.id),
  );
  const theme = useTheme();
  const groupedNotifications = groupNotificationsSmart(
    notificationsData as any[],
  );
  const toggleGroup = (type: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const { readNotification, isReadNotificationLoading } =
    useCreateApplicationNote({ setErrorText });

  const handleClearAll = () => {
    setIsClearNotificationLoading(true)
    const options = {
      onSuccess: () => {
        setIsClearNotificationLoading(false)
        setNotificationsData(
          notificationsData?.map((notification) => ({
            ...notification,
            is_read: true,
          })),
        );
      },
      onError: () => {
        setIsClearNotificationLoading(false)
      },
    };

    const notificationIds = notificationsData?.map(
      (notification) => notification.id,
    );

    const idsString = notificationIds.join(',');

    readNotification(
      {
        notification_id: idsString,
        user_id: notificationsData?.[0].user_id,
        is_enterprise :true,
         clear_all :true,
      },
      options,
    );
  };

  useEffect(() => {
    if (enterpriseNotification?.length) {
      const updatedNotifications = enterpriseNotification
        .filter((en) => !readNotificationIds.has(en.id))
        .map((notification: Notification) => ({
          ...notification,
          is_read: false,
        }));
      setNotificationsData(updatedNotifications);
    } else if (isNotificationsFetched && enterpriseNotification?.length === 0) {
      setNotificationsData([]);
    }
  }, [enterpriseNotification]);

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth={notificationsData?.length ? 'sm' : 'xs'}
      fullWidth
    >
      <DialogTitle>
        <IconButton
          sx={{ position: 'absolute', top: 5, right: 5 }}
          onClick={handleClose}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ paddingTop: '30px', paddingBottom: '30px' }}>
        <Stack spacing={2}>
          {errorText && <Alert severity="error">{errorText}</Alert>}
          <Typography
            display="flex"
            justifyContent="center"
            alignItems="center"
            variant="h3"
            color="secondary"
            fontSize="26px"
            fontWeight="bold"
            paddingBottom="5px"
          >
            <img
              src={`${staticFileLocation}img/notifications-modal-heading.png`}
              css={css`
                max-width: 80px;
                width: 100%;
                margin-right: ${theme.spacing(2)};
              `}
              alt="Notifications"
            />
            Notifications
          </Typography>
          {isNotificationsLoading ? (
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              textAlign="center"
              width="100%"
              borderRadius={8}
            >
              <CircularProgress color="success" />
            </Box>
          ) : notificationsData?.filter((item) => !item.is_read)?.length ? (
            <>
              <List>
                {Object.entries(groupedNotifications).map(([type, group]) => {
                  if (group.length >= 3) {
                    const isExpanded = expandedGroups[type];
                    const summaryText = getGroupSummaryText(type, group.length);

                    return (
                      <div key={type}>
                        <ListItem>
                          <ListItemIcon
                            sx={{ minWidth: '30px' }}
                            style={{ marginTop: '3px' }}
                          >
                            <NotificationIcon
                              notification={group[0] as Notification}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography
                                component="div"
                                dangerouslySetInnerHTML={{
                                  __html: summaryText,
                                }}
                              />
                            }
                            style={{ color: 'black' }}
                          />
                          <Button
                            variant="contained"
                            size="small"
                            sx={{ fontSize: '1rem !important' }}
                            startIcon={
                              isExpanded ? (
                                <ArrowDropDown sx={{ fontSize: '34px' }} />
                              ) : (
                                <ArrowRightIcon sx={{ fontSize: '34px' }} />
                              )
                            }
                            onClick={() => toggleGroup(type)}
                          >
                            {isExpanded ? 'Hide' : 'View'}
                          </Button>
                        </ListItem>
                        <Box sx={{ paddingLeft: '15px' }}>
                          {isExpanded &&
                            group.map((notification) => (
                              <NotificationListItem
                                key={notification.id}
                                notification={notification as Notification}
                                setErrorText={setErrorText}
                                handleClose={handleClose}
                                setNotificationsData={setNotificationsData}
                              />
                            ))}
                        </Box>
                      </div>
                    );
                  }
                  return group.map((notification) => (
                    <NotificationListItem
                      key={notification.id}
                      notification={notification as Notification}
                      setErrorText={setErrorText}
                      handleClose={handleClose}
                      setNotificationsData={setNotificationsData}
                    />
                  ));
                })}
              </List>
              <LoadingButton
                loading={isClearNotificationLoading || isReadNotificationLoading}
                variant="outlined"
                startIcon={<CheckOutlinedIcon />}
                sx={{ alignSelf: 'center' }}
                onClick={handleClearAll}
              >
                Clear All
              </LoadingButton>
            </>
          ) : (
            !isNotificationsLoading && (
              <>
                <Alert severity="success" style={{ borderRadius: '6px' }}>
                  All notifications read
                </Alert>
                <Button
                  variant="contained"
                  onClick={handleClose}
                  style={{ borderRadius: '6px' }}
                >
                  Got it
                </Button>
              </>
            )
          )}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default NotificationsModal;
